import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Trash2, Eye, DollarSign, User, Calendar, Loader2 } from 'lucide-react';
import api from '@/utils/api';

const AdminVideosPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // 'recent' ou 'oldest'

  useEffect(() => {
    // Vérifier que c'est un admin
    if (!user || user.role !== 'super_admin') {
      toast.error('Accès refusé - Admin uniquement');
      navigate('/dashboard');
      return;
    }

    fetchVideos();
  }, [user, navigate]);

  const fetchVideos = async () => {
    try {
      // Récupérer toutes les vidéos soumises via "Vendre Mes Vidéos"
      const res = await api.get('/admin/user-videos');
      setVideos(res.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des vidéos');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour trier les vidéos
  const getSortedVideos = () => {
    if (!videos || videos.length === 0) return [];
    
    const sorted = [...videos];
    
    if (sortBy === 'oldest') {
      // Trier par date de sortie (plus anciennes en premier)
      sorted.sort((a, b) => {
        if (!a.date_sortie) return 1;
        if (!b.date_sortie) return -1;
        return new Date(a.date_sortie) - new Date(b.date_sortie);
      });
    } else {
      // Trier par date de sortie (plus récentes en premier)
      sorted.sort((a, b) => {
        if (!a.date_sortie) return 1;
        if (!b.date_sortie) return -1;
        return new Date(b.date_sortie) - new Date(a.date_sortie);
      });
    }
    
    return sorted;
  };

  const sortedVideos = getSortedVideos();

  const handleDelete = async (videoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vidéo ?')) {
      return;
    }

    setDeleting(videoId);
    try {
      await api.delete(`/admin/user-videos/${videoId}`);
      toast.success('Vidéo supprimée avec succès');
      
      // Retirer de la liste
      setVideos(videos.filter(v => v.id !== videoId));
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              🎬 Gestion des Vidéos Utilisateurs
            </h1>
            <p className="text-zinc-400">
              Vidéos soumises via "Vendre Mes Vidéos" - Total: {videos.length}
            </p>
          </div>
          
          {/* Boutons de tri */}
          <div className="flex gap-2">
            <Button
              onClick={() => setSortBy('recent')}
              className={`${sortBy === 'recent' ? 'bg-[#E50914]' : 'bg-zinc-800'} hover:bg-[#E50914]`}
            >
              📅 Plus Récentes
            </Button>
            <Button
              onClick={() => setSortBy('oldest')}
              className={`${sortBy === 'oldest' ? 'bg-[#E50914]' : 'bg-zinc-800'} hover:bg-[#E50914]`}
            >
              🕰️ Plus Anciennes (1 an+)
            </Button>
          </div>
        </div>

        {videos.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-12 text-center">
              <p className="text-zinc-400 text-lg">
                Aucune vidéo soumise pour le moment
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {sortedVideos.map((video) => (
              <Card key={video.id} className="bg-zinc-900 border-zinc-800 hover:border-[#E50914] transition-all">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Miniature */}
                    <div className="w-full md:w-48 h-32 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                      {video.miniature_path ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${video.miniature_path}`}
                          alt={video.titre}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-600">
                          🎬
                        </div>
                      )}
                    </div>

                    {/* Infos */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-white mb-1">
                            {video.titre}
                          </h3>
                          <div className="flex flex-wrap gap-3 text-sm text-zinc-400">
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {video.user_name || 'Utilisateur'}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign size={14} />
                              {video.prix}$
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye size={14} />
                              {video.views || 0} vues
                            </span>
                          </div>
                        </div>

                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          video.status === 'approuvé' 
                            ? 'bg-green-500/20 text-green-500' 
                            : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {video.status}
                        </span>
                      </div>

                      <p className="text-zinc-300 text-sm mb-3 line-clamp-2">
                        {video.description}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          Publié: {formatDate(video.created_at)}
                        </span>
                        {video.date_sortie && (
                          <span className="flex items-center gap-1 bg-purple-900/30 text-purple-400 px-2 py-1 rounded font-semibold">
                            📅 Sortie: {new Date(video.date_sortie).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                        <span className="bg-zinc-800 px-2 py-1 rounded">
                          {video.categorie}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(video.id)}
                          disabled={deleting === video.id}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {deleting === video.id ? (
                            <Loader2 className="animate-spin mr-2" size={16} />
                          ) : (
                            <Trash2 className="mr-2" size={16} />
                          )}
                          Supprimer
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${video.video_path}`, '_blank')}
                          className="border-zinc-700 text-white hover:border-[#E50914]"
                        >
                          <Eye className="mr-2" size={16} />
                          Voir la vidéo
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVideosPage;
